import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  AlertCircle,
  Brain,
  Copy,
  Database,
  Eye,
  Loader2,
  Pencil,
  Plus,
  RefreshCcw,
  Search,
  Settings,
  Trash2,
} from 'lucide-react';
import { toast } from 'sonner';

import {
  infiniMemoryApi,
  type InfiniMemoryScopeInput,
  type InfiniMemoryStore,
} from '@/api/infiniMemory.api';
import { fullErrorText } from '@/api/errors';
import type { InfiniMemoryDetail, InfiniMemoryItem } from '@/proto/agentplatform/httpv2/memory_pb';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { Textarea } from '@/components/ui/textarea';
import { cn } from '@/lib/utils';

const PAGE_SIZE = 30;
const MEMORY_KIND_LABELS = {
  all: '全部类型',
  current: '工作记忆',
  memory: '长期记忆',
} as const;

type MemoryKindFilter = keyof typeof MEMORY_KIND_LABELS;

interface CachedScope {
  storeId: string;
}

function formatDate(value: string): string {
  if (!value) return '-';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : date.toLocaleString('zh-CN', { hour12: false });
}

function storageKey(prefix: string): string {
  return `infini-memory.scope.${prefix}`;
}

function readCachedScope(prefix: string): CachedScope | null {
  try {
    const raw = localStorage.getItem(storageKey(prefix));
    if (!raw) return null;

    const parsed: unknown = JSON.parse(raw);
    if (
      typeof parsed !== 'object' ||
      parsed === null ||
      !('storeId' in parsed) ||
      typeof parsed.storeId !== 'string'
    ) {
      return null;
    }
    return { storeId: parsed.storeId };
  } catch {
    return null;
  }
}

function cacheScope(prefix: string, storeId: string): void {
  try {
    localStorage.setItem(storageKey(prefix), JSON.stringify({ storeId }));
  } catch {
    // Storage can be unavailable in privacy mode; the page remains usable without persistence.
  }
}

function MemoryPreview({ preview, onView }: { preview: string; onView: () => void }) {
  const sourceTruncated = preview.endsWith('…');
  const lines = (sourceTruncated ? preview.slice(0, -1) : preview).split(/\r?\n/);
  let visibleLineCount = 0;
  let nonEmptyLineCount = 0;
  for (const line of lines) {
    if (line.trim()) {
      if (nonEmptyLineCount === 3) break;
      nonEmptyLineCount += 1;
    }
    visibleLineCount += 1;
  }
  const visiblePreview = lines.slice(0, visibleLineCount).join('\n');
  const clipped = sourceTruncated || lines.slice(visibleLineCount).some((line) => line.trim());

  return (
    <div>
      <pre className="whitespace-pre-wrap break-words rounded-lg bg-muted/60 p-3 font-sans text-sm leading-6">
        {visiblePreview}
        {clipped && <span className="block text-muted-foreground" aria-label="内容未显示完">…</span>}
      </pre>
      {clipped && (
        <div className="mt-1">
          <Button variant="ghost" size="sm" className="h-auto px-0 py-0 text-xs text-muted-foreground hover:bg-transparent hover:text-foreground" onClick={onView}>
            查看完整内容
          </Button>
        </div>
      )}
    </div>
  );
}

export default function InfiniMemory() {
  const queryClient = useQueryClient();
  const [storeId, setStoreId] = useState('');
  const [hydratedPrefix, setHydratedPrefix] = useState('');
  const [query, setQuery] = useState('');
  const [kind, setKind] = useState<MemoryKindFilter>('all');
  const [offset, setOffset] = useState(0);
  const [selectedId, setSelectedId] = useState('');
  const [detailMode, setDetailMode] = useState<'view' | 'edit' | null>(null);
  const [editContent, setEditContent] = useState('');
  const [createOpen, setCreateOpen] = useState(false);
  const [createContent, setCreateContent] = useState('');
  const [deleteTarget, setDeleteTarget] = useState<InfiniMemoryItem | null>(null);
  const [storeManagerOpen, setStoreManagerOpen] = useState(false);
  const [storeFormOpen, setStoreFormOpen] = useState(false);
  const [editingStore, setEditingStore] = useState<InfiniMemoryStore | null>(null);
  const [storeForm, setStoreForm] = useState({ name: '', description: '' });

  const scopeQuery = useQuery({
    queryKey: ['infini-memory', 'scope'],
    queryFn: infiniMemoryApi.getScope,
  });
  const scope = scopeQuery.data;

  const storesQuery = useQuery({
    queryKey: ['infini-memory', 'store-configs'],
    queryFn: () => infiniMemoryApi.listStoreConfigs(scope!.prefix),
    enabled: Boolean(scope?.available && scope.prefix),
  });
  const configuredStores = useMemo(() => storesQuery.data?.items ?? [], [storesQuery.data]);
  const configuredStoreIds = useMemo(
    () => new Set(configuredStores.map((store) => store.id)),
    [configuredStores]
  );

  useEffect(() => {
    if (!scope?.available || storesQuery.isLoading) return;
    if (hydratedPrefix !== scope.prefix) {
      const cached = readCachedScope(scope.prefix);
      const cachedStoreId = cached?.storeId.trim() ?? '';
      const defaultStoreId = scope.storeId.trim();
      setStoreId(
        configuredStoreIds.has(cachedStoreId)
          ? cachedStoreId
          : configuredStoreIds.has(defaultStoreId)
            ? defaultStoreId
            : (configuredStores[0]?.id ?? '')
      );
      setHydratedPrefix(scope.prefix);
      return;
    }
    setStoreId((current) =>
      configuredStoreIds.has(current) ? current : (configuredStores[0]?.id ?? '')
    );
  }, [
    configuredStoreIds,
    configuredStores,
    hydratedPrefix,
    scope?.available,
    scope?.prefix,
    scope?.storeId,
    storesQuery.isLoading,
  ]);

  useEffect(() => {
    if (!scope?.available || hydratedPrefix !== scope.prefix || !configuredStoreIds.has(storeId))
      return;
    cacheScope(scope.prefix, storeId.trim());
  }, [configuredStoreIds, scope?.available, scope?.prefix, hydratedPrefix, storeId]);

  const activeScope: InfiniMemoryScopeInput | null = useMemo(
    () =>
      scope?.available && scope.prefix && configuredStoreIds.has(storeId)
        ? { prefix: scope.prefix, storeId: storeId.trim() }
        : null,
    [configuredStoreIds, scope, storeId]
  );

  const listKey = [
    'infini-memory',
    'list',
    activeScope?.prefix,
    activeScope?.storeId,
    query,
    kind,
    offset,
    PAGE_SIZE,
  ] as const;
  const memoriesQuery = useQuery({
    queryKey: listKey,
    queryFn: () =>
      infiniMemoryApi.listMemories(activeScope!, { query, kind, offset, limit: PAGE_SIZE }),
    enabled: Boolean(activeScope),
  });

  const detailQuery = useQuery({
    queryKey: ['infini-memory', 'detail', activeScope?.prefix, activeScope?.storeId, selectedId],
    queryFn: () => infiniMemoryApi.getMemory(selectedId, activeScope!),
    enabled: Boolean(activeScope && selectedId && detailMode),
  });

  useEffect(() => {
    setOffset(0);
    setSelectedId('');
    setDetailMode(null);
  }, [storeId, scope?.prefix, kind, query]);

  useEffect(() => {
    if (detailMode === 'edit' && detailQuery.data) setEditContent(detailQuery.data.content);
  }, [detailMode, detailQuery.data]);

  const invalidateScope = async () => {
    await queryClient.invalidateQueries({
      queryKey: ['infini-memory', 'list', activeScope?.prefix, activeScope?.storeId],
    });
  };

  const invalidateStores = async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ['infini-memory', 'store-configs'] }),
      queryClient.invalidateQueries({ queryKey: ['infini-memory', 'stores'] }),
    ]);
  };

  const openCreateStore = () => {
    setEditingStore(null);
    setStoreForm({ name: '', description: '' });
    setStoreManagerOpen(false);
    setStoreFormOpen(true);
  };

  const openEditStore = (store: InfiniMemoryStore) => {
    setEditingStore(store);
    setStoreForm({ name: store.name, description: store.description });
    setStoreManagerOpen(false);
    setStoreFormOpen(true);
  };

  const storeSubmitMutation = useMutation({
    mutationFn: () => {
      if (editingStore) {
        return infiniMemoryApi.updateStore(editingStore.id, {
          name: storeForm.name.trim(),
          description: storeForm.description.trim(),
        });
      }
      return infiniMemoryApi.createStore({
        name: storeForm.name.trim(),
        description: storeForm.description.trim(),
      });
    },
    onSuccess: async (savedStore) => {
      setStoreFormOpen(false);
      await invalidateStores();
      if (!editingStore) setStoreId(savedStore.id);
      toast.success(editingStore ? '记忆库已更新' : '记忆库已创建');
    },
    onError: (error) => toast.error(fullErrorText(error)),
  });

  const storeDeleteMutation = useMutation({
    mutationFn: (id: string) => infiniMemoryApi.deleteStore(id),
    onSuccess: async () => {
      await invalidateStores();
      toast.success('记忆库配置已删除');
    },
    onError: (error) => toast.error(fullErrorText(error)),
  });

  const createMutation = useMutation({
    mutationFn: () => infiniMemoryApi.createMemory(activeScope!, createContent.trim()),
    onSuccess: async () => {
      toast.success('记忆已新增');
      setCreateOpen(false);
      setCreateContent('');
      await invalidateScope();
    },
    onError: (error) => toast.error(fullErrorText(error)),
  });

  const updateMutation = useMutation({
    mutationFn: (detail: InfiniMemoryDetail) =>
      infiniMemoryApi.updateMemory(detail.id, activeScope!, editContent, detail.revision),
    onSuccess: async () => {
      toast.success('记忆已更新');
      setDetailMode(null);
      setSelectedId('');
      await invalidateScope();
    },
    onError: (error) => toast.error(fullErrorText(error)),
  });

  const deleteMutation = useMutation({
    mutationFn: (item: InfiniMemoryItem) => infiniMemoryApi.deleteMemory(item.id, activeScope!),
    onSuccess: async () => {
      toast.success('记忆已删除');
      setDeleteTarget(null);
      await invalidateScope();
    },
    onError: (error) => toast.error(fullErrorText(error)),
  });

  if (scopeQuery.isLoading) {
    return (
      <Card className="p-10 text-center shadow-sm">
        <RefreshCcw className="mx-auto mb-3 h-6 w-6 animate-spin text-primary" />
        <p className="text-sm text-muted-foreground">正在加载 infini-memory 配置…</p>
      </Card>
    );
  }
  if (scopeQuery.error) {
    return (
      <Card className="border-destructive/30 bg-destructive/5 p-5 shadow-sm">
        <div className="flex items-start gap-2 text-sm text-destructive">
          <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
          <span>{fullErrorText(scopeQuery.error)}</span>
        </div>
      </Card>
    );
  }
  if (!scope?.available) {
    return (
      <Card className="border-amber-500/30 bg-amber-500/5 p-5 shadow-sm">
        <div className="flex items-start gap-3 text-amber-800 dark:text-amber-300">
          <AlertCircle className="mt-0.5 h-5 w-5 shrink-0" />
          <div>
            <p className="text-sm font-medium">infini-memory 暂不可用</p>
            <p className="mt-1 text-xs leading-5 opacity-80">
              {scope?.unavailableReason || '平台尚未完成配置'}
            </p>
          </div>
        </div>
      </Card>
    );
  }

  const data = memoriesQuery.data;
  const total = data?.total ?? 0;
  const page = Math.floor(offset / PAGE_SIZE) + 1;
  const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <div className="space-y-5">
      <div className="grid items-start gap-5 lg:grid-cols-[300px_minmax(0,1fr)]">
        <Card
          role="region"
          aria-labelledby="infini-memory-scope-title"
          className="shadow-sm lg:sticky lg:top-4"
        >
          <CardHeader className="p-4 pb-3">
            <CardTitle id="infini-memory-scope-title" className="flex items-center gap-2 text-base">
              <Database className="h-4 w-4 text-primary" aria-hidden="true" />
              infini-memory 记忆管理
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4 p-4 pt-0">
            <div className="space-y-4 border-t pt-4">
              <div className="space-y-1.5">
                <Label htmlFor="infini-memory-store">Store ID</Label>
                <div className="flex items-center gap-1">
                  <div className="min-w-0 flex-1">
                    <Select value={storeId} onValueChange={setStoreId}>
                      <SelectTrigger
                        id="infini-memory-store"
                        className="h-9 w-full"
                        disabled={storesQuery.isLoading || configuredStores.length === 0}
                      >
                        <SelectValue
                          placeholder={storesQuery.isLoading ? '正在加载…' : '请选择记忆库'}
                        >
                          {configuredStores.find((store) => store.id === storeId)?.name || storeId}
                        </SelectValue>
                      </SelectTrigger>
                      <SelectContent searchable searchPlaceholder="搜索记忆库...">
                        {configuredStores.map((store) => (
                          <SelectItem
                            key={store.id}
                            value={store.id}
                            searchKeywords={`${store.name} ${store.id}`}
                          >
                            <div className="min-w-0">
                              <div className="truncate">{store.name}</div>
                              <div className="truncate font-mono text-xs text-muted-foreground">
                                {store.id}
                              </div>
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="h-9 w-9 shrink-0 p-0"
                    aria-label="管理记忆库"
                    title="管理记忆库"
                    onClick={() => setStoreManagerOpen(true)}
                  >
                    <Settings className="h-4 w-4" aria-hidden="true" />
                  </Button>
                </div>
                {storesQuery.error ? (
                  <span className="text-xs leading-5 text-destructive">
                    {fullErrorText(storesQuery.error)}
                  </span>
                ) : (
                  !storesQuery.isLoading &&
                  configuredStores.length === 0 && (
                    <span className="text-xs leading-5 text-muted-foreground">
                      暂无已配置的记忆库，请点击“设置”创建。
                    </span>
                  )
                )}
              </div>
            </div>

            <section
              aria-labelledby="infini-memory-quick-actions-title"
              className="space-y-2 border-t pt-4"
            >
              <p id="infini-memory-quick-actions-title" className="text-xs text-muted-foreground">
                快速操作
              </p>
              <Button size="sm" onClick={() => setCreateOpen(true)} disabled={!activeScope}>
                <Plus className="mr-1 h-4 w-4" aria-hidden="true" />
                新增记忆
              </Button>
            </section>

            <div className="space-y-2 border-t pt-4">
              <p className="text-xs text-muted-foreground">使用说明</p>
              <div className="rounded-lg bg-muted/60 p-3 text-xs leading-5 text-muted-foreground">
                <p>
                  <strong className="font-medium text-foreground">新增</strong>
                  会以显式事实追加到当前实例的工作记忆。
                </p>
                <p className="mt-1">
                  <strong className="font-medium text-foreground">删除</strong>
                  仅适用于长期记忆；工作记忆需等待记忆整理交接。
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <main className="min-w-0 space-y-4">
          <Card className="p-4 shadow-sm">
            <div className="flex flex-col gap-3 md:flex-row">
              <div className="relative flex-1">
                <Search
                  className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
                  aria-hidden="true"
                />
                <Input
                  className="pl-9"
                  placeholder="搜索标题、摘要、目录、ID 或正文"
                  value={query}
                  onChange={(event) => setQuery(event.target.value)}
                />
              </div>
              <Select value={kind} onValueChange={(value) => setKind(value as MemoryKindFilter)}>
                <SelectTrigger className="w-full md:w-40" aria-label="记忆类型">
                  <SelectValue>{MEMORY_KIND_LABELS[kind]}</SelectValue>
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">全部类型</SelectItem>
                  <SelectItem value="current">工作记忆</SelectItem>
                  <SelectItem value="memory">长期记忆</SelectItem>
                </SelectContent>
              </Select>
              <Button
                variant="outline"
                onClick={() => memoriesQuery.refetch()}
                disabled={!activeScope || memoriesQuery.isFetching}
              >
                <Search className="mr-2 h-4 w-4" aria-hidden="true" />
                查询
              </Button>
            </div>
          </Card>

          <div className="flex flex-wrap items-center justify-between gap-2 px-1 text-xs text-muted-foreground">
            <span>
              匹配 {total} 条 · 第 {page} / {pageCount} 页
            </span>
            <span>按更新时间从新到旧</span>
          </div>

          {memoriesQuery.error && (
            <Card className="border-destructive/30 bg-destructive/5 p-4 shadow-sm">
              <div className="flex items-start gap-2 text-sm text-destructive">
                <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
                <span>{fullErrorText(memoriesQuery.error)}</span>
              </div>
            </Card>
          )}

          {memoriesQuery.isLoading ? (
            <div className="space-y-3" aria-label="正在加载记忆">
              {[0, 1, 2].map((item) => (
                <Card key={item} className="p-5 shadow-sm">
                  <div className="flex justify-between gap-4">
                    <div className="flex-1 space-y-3">
                      <Skeleton className="h-5 w-20" />
                      <Skeleton className="h-5 w-2/5" />
                    </div>
                    <Skeleton className="h-8 w-32" />
                  </div>
                  <Skeleton className="mt-4 h-16 w-full" />
                </Card>
              ))}
            </div>
          ) : data?.items.length ? (
            <div className="space-y-3">
              {data.items.map((item) => (
                <Card key={item.id} className="shadow-sm transition-colors hover:border-primary/30">
                  <CardHeader className="p-5 pb-0">
                    <div className="flex flex-wrap items-start justify-between gap-3">
                      <div className="min-w-0 flex-1">
                        <Badge
                          variant="secondary"
                          className={cn(
                            'rounded-full border-0 px-2 py-0.5 text-[11px] font-medium',
                            item.kind === 'current'
                              ? 'bg-amber-500/10 text-amber-700 hover:bg-amber-500/10 dark:text-amber-400'
                              : 'bg-emerald-500/10 text-emerald-700 hover:bg-emerald-500/10 dark:text-emerald-400'
                          )}
                        >
                          {item.kind === 'current' ? '工作记忆' : '长期记忆'}
                        </Badge>
                        <CardTitle className="mt-2 break-words text-base leading-6">
                          {item.title || item.id}
                        </CardTitle>
                        {item.summary && item.summary !== item.title && (
                          <p className="mt-1 text-xs leading-5 text-muted-foreground">
                            {item.summary}
                          </p>
                        )}
                      </div>
                      <div className="shrink-0 text-right text-[11px] leading-5 text-muted-foreground">
                        <div>最后更新时间：{formatDate(item.updatedAt)}</div>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent className="p-5 pt-4">
                    <MemoryPreview
                      preview={item.preview}
                      onView={() => {
                        setSelectedId(item.id);
                        setDetailMode('view');
                      }}
                    />
                    <div className="mt-4 flex flex-wrap items-center justify-between gap-3 border-t pt-3">
                      <button
                        type="button"
                        className="inline-flex min-w-0 max-w-full items-center gap-1.5 truncate rounded px-1 py-1 font-mono text-[11px] text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
                        title="复制记忆 ID"
                        onClick={() => {
                          void navigator.clipboard.writeText(item.id);
                          toast.success('已复制记忆 ID');
                        }}
                      >
                        <Copy className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
                        <span className="truncate">{item.id}</span>
                      </button>
                      <div className="flex gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            setSelectedId(item.id);
                            setDetailMode('view');
                          }}
                        >
                          <Eye className="mr-1.5 h-3.5 w-3.5" aria-hidden="true" />
                          查看
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            setSelectedId(item.id);
                            setDetailMode('edit');
                          }}
                        >
                          <Pencil className="mr-1.5 h-3.5 w-3.5" aria-hidden="true" />
                          编辑
                        </Button>
                        {item.deletable && (
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-destructive hover:bg-destructive/10 hover:text-destructive"
                            onClick={() => setDeleteTarget(item)}
                          >
                            <Trash2 className="mr-1.5 h-3.5 w-3.5" aria-hidden="true" />
                            删除
                          </Button>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : (
            <Card className="px-6 py-16 text-center shadow-sm">
              <Brain className="mx-auto h-10 w-10 text-muted-foreground/40" aria-hidden="true" />
              <p className="mt-3 text-sm font-medium">没有找到可管理的记忆</p>
              <p className="mt-1 text-xs text-muted-foreground">
                检查作用域和筛选条件，或新增一条显式记忆。
              </p>
            </Card>
          )}

          {(offset > 0 || offset + PAGE_SIZE < total) && (
            <div className="flex items-center justify-end gap-2">
              <Button
                variant="outline"
                size="sm"
                disabled={offset === 0 || memoriesQuery.isFetching}
                onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
              >
                上一页
              </Button>
              <Button
                variant="outline"
                size="sm"
                disabled={offset + PAGE_SIZE >= total || memoriesQuery.isFetching}
                onClick={() => setOffset(offset + PAGE_SIZE)}
              >
                下一页
              </Button>
            </div>
          )}
        </main>
      </div>

      <Dialog open={storeManagerOpen} onOpenChange={setStoreManagerOpen}>
        <DialogContent
          role="dialog"
          aria-labelledby="infini-memory-store-manager-title"
          className="max-w-lg"
        >
          <DialogHeader>
            <DialogTitle id="infini-memory-store-manager-title">记忆库管理</DialogTitle>
            <DialogDescription>
              此处只管理 infini-memory 记忆库配置，与 mem0 记忆库相互隔离。
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="flex justify-end">
              <Button size="sm" onClick={openCreateStore}>
                <Plus className="mr-1 h-4 w-4" aria-hidden="true" />
                新建记忆库
              </Button>
            </div>
            {storesQuery.isLoading ? (
              <div className="space-y-2 py-2" aria-label="正在加载记忆库">
                {[0, 1].map((item) => (
                  <Skeleton key={item} className="h-16 w-full" />
                ))}
              </div>
            ) : configuredStores.length === 0 ? (
              <p className="py-6 text-center text-sm text-muted-foreground">
                暂无记忆库，请点击上方按钮创建
              </p>
            ) : (
              <div className="max-h-80 space-y-2 overflow-auto">
                {configuredStores.map((store) => (
                  <div
                    key={store.id}
                    className={cn(
                      'flex items-center justify-between rounded-md border p-3',
                      store.id === storeId ? 'border-primary bg-accent/50' : 'border-border'
                    )}
                  >
                    <div className="min-w-0 flex-1">
                      <div className="truncate text-sm font-medium">{store.name}</div>
                      <div className="break-all font-mono text-xs text-muted-foreground">
                        {store.id}
                      </div>
                      {store.description && (
                        <div className="mt-0.5 break-all text-xs text-muted-foreground">
                          {store.description}
                        </div>
                      )}
                    </div>
                    <div className="ml-2 flex shrink-0 items-center gap-1">
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-7 px-2"
                        onClick={() => openEditStore(store)}
                      >
                        编辑
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-7 px-2 text-destructive hover:text-destructive"
                        disabled={storeDeleteMutation.isPending}
                        onClick={() => storeDeleteMutation.mutate(store.id)}
                      >
                        删除
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setStoreManagerOpen(false)}>
              关闭
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={storeFormOpen} onOpenChange={setStoreFormOpen}>
        <DialogContent
          role="dialog"
          aria-labelledby="infini-memory-store-form-title"
          className="max-w-md"
        >
          <DialogHeader>
            <DialogTitle id="infini-memory-store-form-title">
              {editingStore ? '编辑记忆库' : '新建记忆库'}
            </DialogTitle>
            <DialogDescription>
              记忆库保存后可作为 infini-memory 的 Store ID 使用。
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            {editingStore && (
              <div className="space-y-2">
                <Label>唯一 ID（不可修改）</Label>
                <div className="rounded bg-muted p-2 font-mono text-sm">{editingStore.id}</div>
              </div>
            )}
            <div className="space-y-2">
              <Label htmlFor="infini-memory-store-name">名称 *</Label>
              <Input
                id="infini-memory-store-name"
                value={storeForm.name}
                maxLength={200}
                placeholder="输入记忆库名称"
                onChange={(event) =>
                  setStoreForm((current) => ({ ...current, name: event.target.value }))
                }
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="infini-memory-store-description">描述</Label>
              <Textarea
                id="infini-memory-store-description"
                value={storeForm.description}
                rows={3}
                maxLength={500}
                placeholder="可选，简单描述该记忆库的用途"
                onChange={(event) =>
                  setStoreForm((current) => ({ ...current, description: event.target.value }))
                }
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setStoreFormOpen(false)}>
              取消
            </Button>
            <Button
              disabled={!storeForm.name.trim() || storeSubmitMutation.isPending}
              onClick={() => storeSubmitMutation.mutate()}
            >
              {storeSubmitMutation.isPending && (
                <Loader2 className="mr-1 h-4 w-4 animate-spin" aria-hidden="true" />
              )}
              {editingStore ? '保存' : '创建'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>新增显式记忆</DialogTitle>
            <DialogDescription>
              内容会直接追加到当前实例的工作记忆，不会调用 LLM 自动提取。
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="rounded-md bg-amber-500/10 p-3 text-xs leading-5 text-amber-800 dark:text-amber-300">
              运行记忆整理后，符合条件的内容才会成为长期记忆。
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="infini-memory-content">记忆内容</Label>
              <Textarea
                id="infini-memory-content"
                rows={10}
                placeholder="例如：用户偏爱蓝莓。"
                value={createContent}
                onChange={(event) => setCreateContent(event.target.value)}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateOpen(false)}>
              取消
            </Button>
            <Button
              disabled={!createContent.trim() || createMutation.isPending}
              onClick={() => createMutation.mutate()}
            >
              {createMutation.isPending ? '写入中…' : '写入工作记忆'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={Boolean(detailMode)}
        onOpenChange={(open) => {
          if (!open) {
            setDetailMode(null);
            setSelectedId('');
          }
        }}
      >
        <DialogContent
          role="dialog"
          aria-labelledby="infini-memory-detail-title"
          className="max-h-[85vh] max-w-4xl overflow-y-auto"
        >
          <DialogHeader>
            <DialogTitle id="infini-memory-detail-title">
              {detailMode === 'edit' ? '编辑记忆正文' : '记忆详情'}
            </DialogTitle>
          </DialogHeader>
          {detailQuery.isLoading ? (
            <div className="py-12 text-center text-sm text-muted-foreground">正在读取最新内容…</div>
          ) : detailQuery.error ? (
            <div className="flex items-start gap-2 text-sm text-destructive">
              <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
              <span>{fullErrorText(detailQuery.error)}</span>
            </div>
          ) : detailQuery.data ? (
            <div className="space-y-4">
              <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                <Badge
                  variant="secondary"
                  className={cn(
                    'rounded-full border-0',
                    detailQuery.data.kind === 'current'
                      ? 'bg-amber-500/10 text-amber-700 dark:text-amber-400'
                      : 'bg-emerald-500/10 text-emerald-700 dark:text-emerald-400'
                  )}
                >
                  {detailQuery.data.kind === 'current' ? '工作记忆' : '长期记忆'}
                </Badge>
                <span className="font-mono">{detailQuery.data.id}</span>
              </div>
              <div className="break-all rounded-md bg-muted p-3 font-mono text-xs text-muted-foreground">
                路径：{detailQuery.data.path}
              </div>
              {detailMode === 'edit' ? (
                <Textarea
                  rows={18}
                  className="font-mono"
                  value={editContent}
                  onChange={(event) => setEditContent(event.target.value)}
                />
              ) : (
                <pre className="max-h-[55vh] overflow-auto whitespace-pre-wrap break-words rounded-md bg-muted p-4 text-sm leading-7">
                  {detailQuery.data.content}
                </pre>
              )}
            </div>
          ) : null}
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setDetailMode(null);
                setSelectedId('');
              }}
            >
              关闭
            </Button>
            {detailMode === 'view' && detailQuery.data && (
              <Button onClick={() => setDetailMode('edit')}>编辑正文</Button>
            )}
            {detailMode === 'edit' && detailQuery.data && (
              <Button
                disabled={
                  !editContent.trim() ||
                  editContent === detailQuery.data.content ||
                  updateMutation.isPending
                }
                onClick={() => updateMutation.mutate(detailQuery.data!)}
              >
                {updateMutation.isPending ? '保存中…' : '保存'}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={Boolean(deleteTarget)} onOpenChange={() => setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>删除长期记忆</AlertDialogTitle>
            <AlertDialogDescription>
              确定删除“{deleteTarget?.title || deleteTarget?.id}”吗？此操作不可撤销。
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>取消</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              disabled={deleteMutation.isPending}
              onClick={() => deleteTarget && deleteMutation.mutate(deleteTarget)}
            >
              {deleteMutation.isPending ? '删除中…' : '确认删除'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
